import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { SWState } from '@/enums/SWState.enum';
import { SyncActivity } from '@/serviceworker/types';
import { SyncNotificationPayload } from '@/types/SyncNotificationPayload';
import { UploadTrackingImage } from '@/types/UploadTrackingImage';
import { UploadTrackingInspection } from '@/types/UploadTrackingInspection';
import { InspectUploadStatus } from '@/types/InspectUploadStatus';
import { createSlice, PayloadAction, WritableDraft } from '@reduxjs/toolkit';
import { SyncStatus } from '../../enums/SyncStatus.enum';
import { SyncState } from '../types/SyncState';
import { logout } from './authSlice';
import { t } from 'i18next';

// Progress calculation constants
const PROGRESS_WEIGHTS = {
  IMAGE_UPLOAD: 90, // 90% of progress for image uploads
  INSPECTION_DATA: 10, // 10% of progress for inspection data upload
  COMPLETE: 100, // 100% for complete progress
} as const;

const UPLOAD_MESSAGE = {
  UPLOADING: 'Uploading inspection(s)...',
  UPLOADED: 'Uploaded inspection(s) successfully.',
  FAILED: 'Inspection(s) upload failed. Please try again.',
} as const;

const initialState: SyncState = {
  message: '',
  status: SyncStatus.IDLE,
  toUpload: [],
  inspectionId: '',
  completed: [],
  failed: [],
  progress: 0,
  totalImagesUploaded: 0,
  totalImages: 0,
};

const resetUploadState = (state: WritableDraft<SyncState>): void => {
  state.status = SyncStatus.IDLE;
  state.inspectionId = '';
  state.toUpload = [];
  state.completed = [];
  state.failed = [];
  state.message = '';
  state.progress = 0;
  state.totalImagesUploaded = 0;
  state.totalImages = 0;
};

const uploadProgressSlice = createSlice({
  name: 'uploadProgress',
  initialState,
  reducers: {
    updateProgress: (state, action: PayloadAction<SyncNotificationPayload>) => {
      switch (action.payload.status) {
        case SWState.PROCESSING:
          state.status = SyncStatus.IN_PROGRESS;
          state.inspectionId = action.payload.currentInspectionId ?? '';
          break;
        case SWState.COMPLETED:
          calculateAllProgress(state);
          state.message = getDisplayMessage(state);
          state.inspectionId = '';
          state.status = SyncStatus.COMPLETED;
          break;
        case SWState.IDLE:
          resetUploadState(state);
          break;
        case SWState.STARTING:
          state.status = SyncStatus.IN_PROGRESS;
          state.toUpload = action.payload.trackInspection ?? [];
          state.completed = [];
          state.failed = [];
          state.message = UPLOAD_MESSAGE.UPLOADING;
          break;
        default:
          console.error('========================>> Unknown state', action.payload);
      }
    },
    inspectionUploadStarted: (state, action: PayloadAction<SyncActivity>) => {
      if (!action.payload.inspectionId) {
        return;
      }

      const inspection = state.toUpload.find((i) => i.id === action.payload.inspectionId);
      if (!inspection) {
        return;
      }

      state.inspectionId = action.payload.inspectionId;
      state.message = UPLOAD_MESSAGE.UPLOADING;
      state.totalImages = inspection.fullProductImages?.length ?? 0;
      state.progress = 0;
      state.totalImagesUploaded = 0;
    },
    imageUploadStatus: (state, action: PayloadAction<SyncActivity>) => {
      if (action.payload.inspectionId) {
        const inspection = state.toUpload.find((i) => i.id === action.payload.inspectionId);
        if (!inspection) {
          return;
        }
        const defectImagesCount =
          inspection.defects?.reduce((acc, d) => acc + (d.images?.length ?? 0), 0) ?? 0;
        const fullProductImagesCount = inspection.fullProductImages?.length ?? 0;
        const verificationImagesCount = inspection.verificationImages?.length ?? 0;
        state.totalImages = fullProductImagesCount + defectImagesCount + verificationImagesCount;
        switch (action.payload.imageType) {
          case InspectionImageType.DEFECT:
            updateDefectImageStatus(inspection, action.payload.imageId, action.payload.isUploaded);
            break;
          case InspectionImageType.FULL_PRODUCT:
            updateFullProductImageStatus(
              inspection,
              action.payload.imageId,
              action.payload.isUploaded
            );
            break;
          case InspectionImageType.VERIFICATION:
            updateVerificationImageStatus(
              inspection,
              action.payload.imageId,
              action.payload.isUploaded
            );
            break;
          default:
            console.error(
              '========================>> Unknown image type',
              action.payload.imageType
            );
            break;
        }

        const uploadedFullProductImages =
          inspection.fullProductImages?.filter((m) => m.isUploaded).length ?? 0;
        const uploadedDefectImages =
          inspection.defects?.reduce((acc, d) => {
            return acc + (d.images?.filter((m) => m.isUploaded).length ?? 0);
          }, 0) ?? 0;
        const uploadedVerificationImages =
          inspection.verificationImages?.filter((m) => m.isUploaded).length ?? 0;
        state.totalImagesUploaded =
          uploadedFullProductImages + uploadedDefectImages + uploadedVerificationImages;
        state.message = UPLOAD_MESSAGE.UPLOADING;

        // Calculate progress (90% max for image uploads)
        state.progress =
          (state.totalImagesUploaded / state.totalImages) * PROGRESS_WEIGHTS.IMAGE_UPLOAD; // keep 10% for inspection data upload

        // Update inspection in toUpload array
        updateInspectionInArray(state, action.payload.inspectionId, inspection);
        console.log(
          '[uploadProgressSlice] imageUploadStatus => state=> ',
          JSON.stringify(state, null, 4)
        );
      }
    },
    imageUploadCompleted: (state, action: PayloadAction<SyncActivity>) => {
      const inspection = findInspectionById(state, action.payload.inspectionId);
      if (!inspection) {
        return;
      }
    },
    inspectionDataUploadStatus: (state, action: PayloadAction<SyncActivity>) => {
      console.log('[uploadProgressSlice] inspectionDataUploadStatus=> ', action.payload);
      const inspection = findInspectionById(state, action.payload.inspectionId);
      if (!inspection) {
        return;
      }

      state.inspectionId = inspection.id ?? '';
      state.status = SyncStatus.IN_PROGRESS;

      // Update the inspection in the array with uploaded status and error
      updateInspectionInArray(state, inspection.id, {
        ...inspection,
        isUploaded: action.payload.isUploaded,
        error: action.payload.response?.error,
      });

      // Get the updated inspection reference
      const updatedInspection = state.toUpload.find((i) => i.id === inspection.id);
      if (!updatedInspection) return;

      state.totalImagesUploaded =
        updatedInspection.fullProductImages?.filter((m) => m.isUploaded).length ?? 0;
      state.totalImages = updatedInspection.fullProductImages?.length ?? 0;
      if (action.payload.isUploaded) {
        state.progress =
          (state.totalImagesUploaded / state.totalImages) * PROGRESS_WEIGHTS.COMPLETE;
      } else {
        state.progress =
          (state.totalImagesUploaded / state.totalImages) * PROGRESS_WEIGHTS.IMAGE_UPLOAD; // keep 10% for inspection data upload
      }
      if (action.payload.response?.ok === false) {
        switch (action.payload.response?.status) {
          case 400:
            updatedInspection.errorMessage = t('sync_validation_failed');
            break;
          case 404:
            updatedInspection.errorMessage = t('sync_deleted');
            break;
          case 422:
            updatedInspection.errorMessage = t('sync_unprocessable');
            break;
          case 409:
            updatedInspection.errorMessage = t('sync_conflict');
            break;
          case 500:
            updatedInspection.errorMessage = t('sync_internal_server_error');
            break;
        }
      }
      calculateInspectionProgress(state, updatedInspection);
    },
    inspectionUpdating: (state, action: PayloadAction<SyncActivity>) => {
      console.log('[uploadProgressSlice] inspectionUpdating=> ', action.payload);
      const inspection = findInspectionById(state, action.payload.inspectionId);
      if (!inspection) {
        return;
      }
      if (action.payload.inspectionId) {
        state.inspectionId = action.payload.inspectionId;
      }
      state.message = UPLOAD_MESSAGE.UPLOADING;
    },
    resetUploadProgress: (state) => {
      resetUploadState(state);
    },
    clearProgress: (state) => {
      resetUploadState(state);
    },
    updateMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState); // Reset state on logout
  },
});
function getDisplayMessage(state: SyncState): string {
  if (state.failed?.length > 0) {
    const inspectionMessages = state.failed.filter((i) => !!i.errorMessage);
    if (inspectionMessages.length > 0) {
      return inspectionMessages[0].errorMessage || UPLOAD_MESSAGE.FAILED;
    }
    return UPLOAD_MESSAGE.FAILED;
  }
  return UPLOAD_MESSAGE.UPLOADED;
}

function calculateInspectionProgress(
  state: SyncState,
  currentInspection: UploadTrackingInspection
) {
  const isDefectImageUploadFailed = currentInspection.defects?.some((d) =>
    d.images?.some((img: UploadTrackingImage) => img.isUploaded === false)
  );
  const isFullProductImageUploadFailed = currentInspection.fullProductImages?.some(
    (m) => m.isUploaded === false
  );
  const isVerificationImageUploadFailed = currentInspection.verificationImages?.some(
    (m) => m.isUploaded === false
  );
  const isInspectionDataUploadFailed = currentInspection.isUploaded === false;

  switch (true) {
    case isInspectionDataUploadFailed:
      currentInspection.status = InspectUploadStatus.Failed;
      break;
    case isDefectImageUploadFailed:
      currentInspection.status = InspectUploadStatus.FailedImages;
      break;
    case isFullProductImageUploadFailed:
      currentInspection.status = InspectUploadStatus.FailedImages;
      break;
    case isVerificationImageUploadFailed:
      currentInspection.status = InspectUploadStatus.FailedImages;
      break;
    default:
      currentInspection.status = InspectUploadStatus.Success;
      break;
  }
  calculateAllProgress(state);
}

// Helper functions for updating image status
const findInspectionById = (
  state: SyncState,
  inspectionId: string | undefined
): UploadTrackingInspection | undefined => {
  if (!inspectionId) return undefined;
  return state.toUpload.find((i) => i.id === inspectionId);
};

const updateInspectionInArray = (
  state: SyncState,
  inspectionId: string,
  updatedInspection: UploadTrackingInspection
) => {
  state.toUpload = state.toUpload.map((i) => (i.id === inspectionId ? updatedInspection : i));
};

const updateDefectImageStatus = (
  inspection: UploadTrackingInspection,
  imageId: string | undefined,
  isUploaded: boolean | undefined
) => {
  if (!imageId) return;

  inspection.defects = inspection.defects?.map((d) => ({
    ...d,
    images: d.images?.map((img) =>
      img.id === imageId ? { ...img, isUploaded: isUploaded ?? false } : img
    ),
  }));
};

const updateFullProductImageStatus = (
  inspection: UploadTrackingInspection,
  imageId: string | undefined,
  isUploaded: boolean | undefined
) => {
  if (!imageId) return;

  inspection.fullProductImages = inspection.fullProductImages?.map((m) =>
    m.id === imageId ? { ...m, isUploaded: isUploaded ?? false } : m
  );
};

const updateVerificationImageStatus = (
  inspection: UploadTrackingInspection,
  imageId: string | undefined,
  isUploaded: boolean | undefined
) => {
  if (!imageId) return;

  inspection.verificationImages = inspection.verificationImages?.map((m) =>
    m.id === imageId ? { ...m, isUploaded: isUploaded ?? false } : m
  );
};

const calculateAllProgress = (state: SyncState) => {
  // Helper functions to check inspection status
  const hasFailedDefectImages = (inspection: UploadTrackingInspection): boolean => {
    return (
      inspection.defects?.some((defect) => {
        if (!defect.images || defect.images.length === 0) return false;
        return defect.images.some((img: UploadTrackingImage) => img.isUploaded === false);
      }) || false
    );
  };

  const hasFailedFullProductImages = (inspection: UploadTrackingInspection): boolean => {
    return !!(
      inspection.fullProductImages &&
      inspection.fullProductImages.length > 0 &&
      inspection.fullProductImages.some((img) => img.isUploaded === false)
    );
  };

  const hasFailedVerificationImages = (inspection: UploadTrackingInspection): boolean => {
    return !!(
      inspection.verificationImages &&
      inspection.verificationImages.length > 0 &&
      inspection.verificationImages.some((img) => img.isUploaded === false)
    );
  };

  const hasFailedInspectionData = (inspection: UploadTrackingInspection): boolean => {
    return inspection.isUploaded === false;
  };

  const hasAllDefectImagesUploaded = (inspection: UploadTrackingInspection): boolean => {
    return (
      inspection.defects?.every((defect) => {
        if (!defect.images || defect.images.length === 0) return true;
        return defect.images.every((img: UploadTrackingImage) => img.isUploaded === true);
      }) || true
    );
  };

  const hasAllFullProductImagesUploaded = (inspection: UploadTrackingInspection): boolean => {
    if (!inspection.fullProductImages) return true;
    return (
      inspection.fullProductImages.length === 0 ||
      inspection.fullProductImages.every((img) => img.isUploaded === true)
    );
  };

  const hasAllVerificationImagesUploaded = (inspection: UploadTrackingInspection): boolean => {
    if (!inspection.verificationImages) return true;
    return (
      inspection.verificationImages.length === 0 ||
      inspection.verificationImages.every((img) => img.isUploaded === true)
    );
  };

  const hasSuccessfulInspectionData = (inspection: UploadTrackingInspection): boolean => {
    return inspection.isUploaded === true;
  };

  // Determine failed inspections
  state.failed = state.toUpload.filter(
    (inspection) =>
      hasFailedDefectImages(inspection) ||
      hasFailedFullProductImages(inspection) ||
      hasFailedVerificationImages(inspection) ||
      hasFailedInspectionData(inspection)
  );

  // Determine completed inspections
  state.completed = state.toUpload.filter(
    (inspection) =>
      hasAllDefectImagesUploaded(inspection) &&
      hasAllFullProductImagesUploaded(inspection) &&
      hasAllVerificationImagesUploaded(inspection) &&
      hasSuccessfulInspectionData(inspection)
  );

  // Calculate overall progress
  state.progress =
    state.toUpload.length > 0
      ? (state.completed.length / state.toUpload.length) * PROGRESS_WEIGHTS.COMPLETE
      : 0;
};

export const {
  updateProgress,
  clearProgress,
  resetUploadProgress,
  imageUploadStatus,
  inspectionDataUploadStatus,
  updateMessage,
  inspectionUploadStarted,
  inspectionUpdating,
  imageUploadCompleted,
} = uploadProgressSlice.actions;
export default uploadProgressSlice.reducer;
